import type { DocumentV1 } from '@alpod-ai-writer/backend';
import clsx from 'clsx';
import { Avatar, AvatarGroup, Badge, Button, Card } from 'flowbite-react';
import { FileTextIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { RouteIndex } from '../../../app/routeIndex';
import { getRandomCursorColor } from '../../../editor/utils/editor.utils';
import { getInitialsFromName } from '../../../lib/identity.utils';
import { pathWithParams } from '../../../lib/routing.utils';

type Props = {
    document: DocumentV1.ListItem;
    className?: string;
}

const DocumentCard = ({ document, className }: Props) => {
    const navigate = useNavigate();

    const avatarColors = useMemo(() => {
        return document.sharedWith.map(() => getRandomCursorColor());
    }, [
        document.sharedWith.map(user => user.identity.identityId).join(',')
    ]);

    return (
        <Card
            renderImage={() => (
                <div className={clsx(
                    'flex flex-col justify-center items-center gap-2 w-full flex-shrink-0 rounded-t-lg aspect-[3/1] h-[10rem]',
                    document.template?.iconPath ? 'bg-white' : 'bg-gray-200'
                )}>
                    {document.template?.iconPath && (
                        <img
                            src={`/storage/${document.template.iconPath}`}
                            alt={document.template.label}
                            className='max-h-[80%] max-w-[90%] w-auto h-auto object-contain'
                        />
                    )}
                    {!document.template?.iconPath && (
                        <FileTextIcon size={48} className='m-auto text-gray-400' />
                    )}
                </div>
            )}
            className={clsx(
                'flex flex-col relative cursor-pointer',
                className
            )}
            onClick={() => navigate(pathWithParams(
                RouteIndex.documentDetail.edit,
                {
                    documentId: document.documentId
                }
            ))}
        >
            <div className='flex-grow flex flex-col gap-2'>
                <h3 className="text-lg font-semibold flex-grow">{ document.documentName }</h3>
            </div>

            <div className='relative flex flex-row items-center justify-start gap-2'>
                <div className="flex grow">
                    {/* <Badge color={getBadgeColor(document.documentType)}>{document.documentType}</Badge> */}
                    {document.paidForAt && (
                        <Badge color='success'>Zaplaceno</Badge>
                    )}
                    {!document.paidForAt && (
                        <Badge color='teal'>Čeká na platbu</Badge>
                    )}
                </div>

                <Avatar
                    key={document.ownerIdentity.identityId}
                    alt={document.ownerIdentity.name}
                    title={document.ownerIdentity.name}
                    placeholderInitials={getInitialsFromName(document.ownerIdentity.name)}
                    size='sm'
                    rounded
                    style={{
                        backgroundColor: '#5048e5',
                        borderRadius: '1000px'
                    }}
                    theme={{
                        root: {
                            base: 'relative p-[1px]',
                            initials: {
                                base: 'flex flex-row items-center justify-center bg-inherit',
                                text: 'text-white text-xs font-semibold'
                            }
                        },
                    }}
                />

                <AvatarGroup>
                    {document.sharedWith.map((user, index) => (
                        <Avatar
                            key={user.identity.identityId}
                            alt={user.identity.name}
                            title={user.identity.name}
                            placeholderInitials={getInitialsFromName(user.identity.name)}
                            size='sm'
                            rounded
                            style={{
                                backgroundColor: avatarColors[index],
                                color: avatarColors[index],
                                borderRadius: '1000px'
                            }}
                            theme={{
                                root: {
                                    base: 'relative p-[1px]',
                                    initials: {
                                        base: 'flex flex-row items-center justify-center bg-white',
                                        text: 'text-xs font-semibold'
                                    }
                                },
                            }}
                        />
                    ))}
                </AvatarGroup>
            </div>

            <Button color='primary' className="flex w-full">Otevřít</Button>
        </Card>
    );
};

export default DocumentCard;
